import { useForm } from '@tanstack/react-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { Button, Input, Label } from '@workspace/shadcn';
import {
  EmailInput,
  FieldMessage,
  FormMessage,
  PasswordInput,
  client,
  errorToFormIssue,
  signUpEmail,
} from '@workspace/ui';

import { AuthShell } from '../components/auth-shell.tsx';
import { enrollDestination } from '../lib/enroll.ts';
import { auth } from '../firebase.tsx';

const schema = z.object({
  name: z.string().min(1, { error: 'Your name is required' }),
  email: z.string().min(1, { error: 'Email is required' }).email(),
  password: z
    .string()
    .min(8, { error: 'Password must be at least 8 characters' }),
});

export default function SignUp() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const course = searchParams.get('course');

  const form = useForm({
    defaultValues: { name: '', email: '', password: '' },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await signUpEmail(auth, value.email, value.password, value.name);
          // The learner row is keyed by the Firebase uid, so it can only be
          // created once the account exists and the token is available.
          await client.request('POST /me/register', { name: value.name });
          await navigate(enrollDestination(course));
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  const signInHref = course ? `/signin?course=${course}` : '/signin';

  return (
    <AuthShell
      title="Create your account"
      subtitle={
        <span className="flex gap-x-1">
          Already have an account?
          <Link to={signInHref} className="text-primary font-medium">
            Sign in
          </Link>
        </span>
      }
    >
      <form
        className="mt-6 space-y-4"
        onSubmit={async (event) => {
          event.preventDefault();
          event.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <form.Field
          name="name"
          children={(field) => (
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
                placeholder="Your name"
              />
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Field
          name="email"
          children={(field) => (
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="email">Email</Label>
              <EmailInput
                id="email"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
              />
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Field
          name="password"
          children={(field) => (
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
              />
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button type="submit" disabled={!canSubmit} className="w-full">
              {isSubmitting ? 'Creating account...' : 'Create account'}
            </Button>
          )}
        />
        <form.Subscribe
          selector={(state) => state.errors}
          children={(errors) => <FormMessage errors={errors} />}
        />
      </form>
    </AuthShell>
  );
}
